import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Link,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import { perlColors, perlTypography, perlSpacing } from "../design/tokens";
import { useAuth } from "../context/AuthContext";
import PerlTextField from "../shared/components/PerlTextField";
import { apiFetch } from "../api/apiClient";
import { config } from "../assets/config";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import {
  APP_WORKSPACE_MODEL_TYPE,
  APP_WORKSPACE_TOWERS,
  buildWorkspaceStatusMap,
  isWorkspaceItemPublished,
} from "../utils/appWorkspaceConfig";

const baseURL = config.VITE_API_BASE_URL;
const MAX_SIGNUP_COMMODITY_SELECTIONS = 2;
const compactSignupFieldSx = {
  mb: 1.1,
  "& .MuiInputBase-root": {
    minHeight: 38,
  },
  "& .MuiInputBase-input": {
    py: 0.85,
    fontSize: "14px",
  },
  "& .MuiInputLabel-root": {
    fontSize: "14px",
  },
  "& .MuiFormHelperText-root": {
    mt: 0.25,
    fontSize: "11px",
  },
};

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();

  // ---- SSO (Azure OAuth) ----
  // Expect these to be provided via Vite env vars
  const __TENANT_ID__ = config.VITE_AZURE_OAUTH_TENANT_ID;
  const __CLIENT_ID__ = config.VITE_AZURE_OAUTH_CLIENT_ID;

  // Must match the Redirect URI registered in Azure (including http/https + path + port)
  const redirectUri = `${window.location.origin}/login`;

  const azureOauthUri = useMemo(() => {
    const tenant = __TENANT_ID__ || "";
    const clientId = __CLIENT_ID__ || "";

    const scope = ["openid", "offline_access", "https://graph.microsoft.com/User.Read"].join(" ");

    const state = crypto?.randomUUID ? crypto.randomUUID() : String(Date.now());
    sessionStorage.setItem("azure_oauth_state", state);

    const params = new URLSearchParams({
      client_id: clientId,
      response_type: "code",
      redirect_uri: redirectUri,
      response_mode: "query",
      scope,
      state,
    });

    return `https://login.microsoftonline.com/${tenant}/oauth2/v2.0/authorize?${params.toString()}`;
  }, [redirectUri, __CLIENT_ID__, __TENANT_ID__]);
  // ---- /SSO (Azure OAuth) ----

  // View states: 'initial', 'signup', 'accessRequested'
  const [view, setView] = useState("initial");
  const [accessStatusText, setAccessStatusText] = useState(
    "Access request submitted and currently under review.",
  );
  const [copied, setCopied] = useState(false);

  const [signupData, setSignupData] = useState({
    firstName: "",
    lastName: "",
    companyName: "",
    industry: "",
    country: "",
    designation: "",
    emailId: "",
    appAccess: [],
  });

  const [errors, setErrors] = useState({});
  const [ssoLoading, setSsoLoading] = useState(false);
  const [ssoError, setSsoError] = useState("");
  const [signupStatusMap, setSignupStatusMap] = useState({});
  const [signupStatusError, setSignupStatusError] = useState("");

  const signupAccessGroups = useMemo(() => {
    return APP_WORKSPACE_TOWERS.map((tower) => {
      const commodityMap = new Map();

      tower.commodities.forEach((commodity) => {
        const accessKey = `${tower.title} - ${commodity.name}`;
        const modelType = commodity.modelType || APP_WORKSPACE_MODEL_TYPE.STANDARD;
        const published = isWorkspaceItemPublished(
          signupStatusMap,
          tower.title,
          commodity.name,
          modelType,
        );

        if (!commodityMap.has(accessKey)) {
          commodityMap.set(accessKey, {
            accessKey,
            commodityName: commodity.name,
            published,
          });
          return;
        }

        if (published) {
          commodityMap.set(accessKey, {
            ...commodityMap.get(accessKey),
            published: true,
          });
        }
      });

      return {
        towerTitle: tower.title,
        options: Array.from(commodityMap.values()).filter((option) => option.published),
      };
    }).filter((group) => group.options.length > 0);
  }, [signupStatusMap]);

  const publishedSignupAccessCount = signupAccessGroups.reduce(
    (total, group) => total + group.options.length,
    0,
  );

  const handleSignupChange = (e) => {
    const { name, value } = e.target;
    setSignupData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSignupAccessToggle = (accessKey) => {
    setSignupData((prev) => {
      const selected = new Set(prev.appAccess || []);
      if (selected.has(accessKey)) {
        selected.delete(accessKey);
      } else {
        if (selected.size >= MAX_SIGNUP_COMMODITY_SELECTIONS) {
          return prev;
        }
        selected.add(accessKey);
      }

      return {
        ...prev,
        appAccess: Array.from(selected),
      };
    });
    if (errors.appAccess) {
      setErrors((prev) => ({ ...prev, appAccess: "" }));
    }
  };

  useEffect(() => {
    if (view !== "signup") return;

    let cancelled = false;

    const loadPublishedStatuses = async () => {
      try {
        setSignupStatusError("");
        const response = await fetch(`${baseURL || ""}/api/app-workspace/public-statuses`);
        if (!response.ok) {
          throw new Error("Unable to load published apps");
        }
        const data = await response.json();
        if (!cancelled) {
          setSignupStatusMap(buildWorkspaceStatusMap(data?.statuses || []));
        }
      } catch (error) {
        console.error("Failed to load signup app list:", error);
        if (!cancelled) {
          setSignupStatusError("Unable to refresh published apps.");
        }
      }
    };

    loadPublishedStatuses();

    return () => {
      cancelled = true;
    };
  }, [view]);

  const validateSignup = () => {
    const newErrors = {};
    const nameRegex = /^[a-zA-Z\s'-]+$/; // Only letters, spaces, hyphens, and apostrophes

    if (!signupData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }
    // Note: removed validation after testing
    //  else if (signupData.firstName.trim().length < 3) {
    //   newErrors.firstName = "First name must be at least 3 characters";
    // }
    else if (!nameRegex.test(signupData.firstName.trim())) {
      newErrors.firstName = "First name can only contain letters";
    }

    if (!signupData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }
    // Note: removed validation after testing
    // else if (signupData.lastName.trim().length < 3) {
    //   newErrors.lastName = "Last name must be at least 3 characters";
    // }
    else if (!nameRegex.test(signupData.lastName.trim())) {
      newErrors.lastName = "Last name can only contain letters";
    }

    if (!signupData.emailId.trim()) {
      newErrors.emailId = "Email is required";
    } else {
      const emailRegex = /^[a-zA-Z0-9][a-zA-Z0-9._%+-]*@[a-zA-Z0-9][a-zA-Z0-9.-]*\.[a-zA-Z]{2,}$/;
      const validTLDs = [
        "com",
        "org",
        "net",
        "edu",
        "gov",
        "mil",
        "int",
        "co",
        "io",
        "ai",
        "app",
        "dev",
        "tech",
        "info",
        "biz",
        "us",
        "uk",
        "ca",
        "au",
        "de",
        "fr",
        "in",
        "jp",
        "cn",
        "br",
        "ru",
        "it",
        "es",
        "nl",
        "se",
        "no",
        "pl",
        "ch",
        "be",
        "at",
        "dk",
        "fi",
        "nz",
        "ie",
        "za",
        "mx",
        "ar",
        "cl",
        "sg",
        "hk",
        "kr",
        "tw",
        "my",
        "th",
        "id",
        "ph",
        "vn",
        "pk",
        "bd",
        "ng",
        "ke",
        "eg",
        "ma",
        "tz",
        "ug",
        "gh",
        "ao",
        "mz",
        "zm",
        "zw",
        "bw",
        "na",
        "ls",
        "sz",
      ];

      if (!emailRegex.test(signupData.emailId)) {
        newErrors.emailId = "Please enter a valid email address";
      } else {
        // Extract TLD from email
        const tld = signupData.emailId.split(".").pop().toLowerCase();
        if (!validTLDs.includes(tld)) {
          newErrors.emailId = "Please enter a valid email address with a recognized domain";
        }
      }
    }
    if (publishedSignupAccessCount > 0 && signupData.appAccess.length === 0) {
      newErrors.appAccess = "Select at least one commodity";
    } else if (signupData.appAccess.length > MAX_SIGNUP_COMMODITY_SELECTIONS) {
      newErrors.appAccess = `Select up to ${MAX_SIGNUP_COMMODITY_SELECTIONS} commodities`;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSSOLogin = () => {
    setSsoLoading(true);
    setSsoError("");
    window.location.assign(azureOauthUri);
  };

  const buildSignupEmailDraft = () => {
    const subject = "Velocity AI Costing Suite - App - Access Request";
    const body = [
      "Hello Admin Team,",
      "",
      "Please grant access to the Velocity AI Costing Suite. Details are provided below:",
      "",
      `First Name: ${signupData.firstName.trim()}`,
      `Last Name: ${signupData.lastName.trim()}`,
      `Company Name: ${signupData.companyName.trim() || "N/A"}`,
      `Industry: ${signupData.industry.trim() || "N/A"}`,
      `Country: ${signupData.country.trim() || "N/A"}`,
      `Designation: ${signupData.designation.trim() || "N/A"}`,
      `Email ID: ${signupData.emailId.trim()}`,
      "",
      "Thank you.",
    ].join("\n");

    return `mailto:admin_perlab_pl@kearney.com?subject=${encodeURIComponent(
      subject,
    )}&body=${encodeURIComponent(body)}`;
  };
  // Handle Azure OAuth callback: /login?code=...&state=...
  useEffect(() => {
    const run = async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get("code");
      if (!code) return;

      setSsoLoading(true);
      setSsoError("");

      try {
        // Exchange auth code for token via backend
        const payload = {
          code,
          redirect_uri: redirectUri,
        };

        const data = Object.keys(payload)
          .map((key) => `${key}=${encodeURIComponent(payload[key])}`)
          .join("&");

        const tokenResp = await fetch(`${baseURL}/api/auth/getAToken`, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: data,
        });

        if (!tokenResp.ok) {
          // Try to surface a backend-provided message
          let errJson = null;
          try {
            errJson = await tokenResp.json();
          } catch {
            // ignore
          }

          const msg =
            errJson?.error?.message ||
            errJson?.payload ||
            "Unable to complete SSO. Please contact the administrator.";

          if (errJson.msg === "ApprovalPending") {
            setAccessStatusText("Access request submitted and currently under review.");
            setView("accessRequested");
            cleanOauthParamsFromUrl();
          } else if (errJson.msg === "ApprovalError") {
            setAccessStatusText("Your account has been deactivated.");
            setView("accessRequested");
            cleanOauthParamsFromUrl();
          } else if (errJson.msg === "AuthenticationError" || errJson.msg === "AccessError") {
            if (errJson.payload.error !== "invalid_grant") {
              setAccessStatusText("Access request submitted and currently under review.");
              setView("accessRequested");
              cleanOauthParamsFromUrl();
            }
          } else {
            setSsoError(msg);
            return;
          }
        }

        const tokenJson = await tokenResp.json();
        if (!tokenJson?.access_token) {
          setSsoError(
            "Unable to complete SSO (missing access token). Please contact the administrator.",
          );
          cleanOauthParamsFromUrl();
          return;
        }

        localStorage.setItem("accessToken", "Bearer " + tokenJson.access_token);
        localStorage.setItem("userImage", tokenJson.image);

        let payloadToken = window.localStorage.getItem("accessToken").slice(7);
        const formData = new FormData();
        formData.append("token", payloadToken);

        const identityResp = await apiFetch(`/api/auth/identity`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${tokenJson.access_token}`,
          },
          body: formData,
        });

        if (!identityResp.ok) {
          setSsoError("Unable to verify your account status. Please contact the administrator.");
          return;
        }

        const userInfo = await identityResp.json();
        const userData = userInfo?.data || userInfo;

        const isActive = Number(userData?.is_active ?? 1);

        if (userInfo?.success === false) {
          const msg = userInfo?.data?.error?.payload || "Something went wrong!";
          setSsoError(msg);
          return;
        }

        if (isActive !== 1) {
          setSsoError(
            "Your account is not active. Please contact the administrator to request access.",
          );
          return;
        }

        await login(
          {
            firstName: userData?.first_name || "Kearney",
            lastName: userData?.last_name || "User",
            email: userData?.email || "user@kearney.com",
            ssoUser: true,
          },
          userData,
        );

        // Clean up URL so refresh doesn't re-run the token exchange
        window.history.replaceState({}, document.title, window.location.pathname);

        navigate("/");
      } catch (error) {
        console.error("Error completing SSO:", error);
        setSsoError("Unable to complete SSO. Please contact the administrator.");
      } finally {
        setSsoLoading(false);
      }
    };

    run();
  }, [login, navigate, redirectUri]);

  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    const emailLower = (signupData.emailId || "").trim().toLowerCase();
    if (emailLower.endsWith("@kearney.com") || emailLower.endsWith("@atkearney.com")) {
      alert("you have access to app please login using sso ");
      return;
    }
    if (validateSignup()) {
      try {
        const url = `${baseURL}/api/admin/signup-request`;
        console.log("🌐 Submitting signup to:", url);

        // Call backend API to create inactive user account
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            firstName: signupData.firstName,
            lastName: signupData.lastName,
            emailId: signupData.emailId,
            designation: signupData.designation,
            companyName: signupData.companyName,
            industry: signupData.industry,
            country: signupData.country,
            appAccess: signupData.appAccess,
          }),
        });

        console.log("📡 Response status:", response.status, response.statusText);
        console.log("📋 Content-Type:", response.headers.get("content-type"));

        // Check if response is JSON before parsing
        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          const text = await response.text();
          console.error("❌ Server returned non-JSON response:", text.substring(0, 500));
          alert(
            `Server error: The signup endpoint is not responding correctly. Status: ${response.status}\n\nPlease ensure the backend server is running.`,
          );
          return;
        }

        const result = await response.json();

        if (!response.ok) {
          // Show error message
          console.error("❌ Signup failed:", result);
          alert(result.error || "Failed to submit signup request. Please try again.");
          return;
        }

        // Success - show confirmation view
        console.log("✅ Signup request submitted:", result);
        setView("accessRequested");
      } catch (error) {
        console.error("❌ Signup request error:", error);
        alert(
          "Network error. Please check your connection and try again.\n\nDetails: " + error.message,
        );
      }
    }
  };

  const copyEmail = async (email) => {
    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
      window.clearTimeout(copyEmail._t);
      copyEmail._t = window.setTimeout(() => setCopied(false), 1200);
    } catch (e) {
      console.error("Clipboard write failed:", e);
    }
  };

  const renderInitialView = () => (
    <>
      {ssoError && (
        <Alert
          severity="warning"
          sx={{
            marginBottom: 2,
            backgroundColor: "rgba(255, 193, 7, 0.12)",
            color: perlColors.textPrimary,
            border: "1px solid rgba(255, 193, 7, 0.3)",
          }}
        >
          {ssoError}
        </Alert>
      )}
      <Button
        fullWidth
        variant="contained"
        onClick={handleSSOLogin}
        disabled={ssoLoading}
        sx={{
          padding: "10px 20px",
          fontSize: "15px",
          fontWeight: 600,
          backgroundColor: perlColors.brandColor,
          color: perlColors.white,
          textTransform: "none",
          marginBottom: 3,
          "&:hover": {
            backgroundColor: perlColors.brandColor,
            color: perlColors.black,
          },
        }}
      >
        {ssoLoading ? "Logging in..." : "Login with SSO"}
      </Button>

      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
        <Link
          href="#"
          onClick={(e) => {
            e.preventDefault();
            setView("signup");
          }}
          sx={{
            fontSize: "14px",
            color: perlColors.textSecondary,
            textDecoration: "none",
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          Sign Up
        </Link>
      </Box>
    </>
  );

  const renderSignupView = () => (
    <>
      <Box component="form" onSubmit={handleSignupSubmit} noValidate>
        <PerlTextField
          fullWidth
          size="small"
          label="First Name *"
          name="firstName"
          value={signupData.firstName}
          onChange={handleSignupChange}
          error={!!errors.firstName}
          helperText={errors.firstName}
          sx={compactSignupFieldSx}
          autoFocus
        />

        <PerlTextField
          fullWidth
          size="small"
          label="Last Name *"
          name="lastName"
          value={signupData.lastName}
          onChange={handleSignupChange}
          error={!!errors.lastName}
          helperText={errors.lastName}
          sx={compactSignupFieldSx}
        />

        <PerlTextField
          fullWidth
          size="small"
          label="Company Name"
          name="companyName"
          value={signupData.companyName}
          onChange={handleSignupChange}
          sx={compactSignupFieldSx}
        />

        <PerlTextField
          fullWidth
          size="small"
          label="Industry"
          name="industry"
          value={signupData.industry}
          onChange={handleSignupChange}
          sx={compactSignupFieldSx}
        />

        <PerlTextField
          fullWidth
          size="small"
          label="Country"
          name="country"
          value={signupData.country}
          onChange={handleSignupChange}
          sx={compactSignupFieldSx}
        />

        <PerlTextField
          fullWidth
          size="small"
          label="Designation"
          name="designation"
          value={signupData.designation}
          onChange={handleSignupChange}
          sx={compactSignupFieldSx}
        />

        <PerlTextField
          fullWidth
          size="small"
          label="Email ID *"
          name="emailId"
          type="email"
          value={signupData.emailId}
          onChange={handleSignupChange}
          error={!!errors.emailId}
          helperText={errors.emailId}
          sx={{ ...compactSignupFieldSx, mb: 1.3 }}
        />

        <Box
          sx={{
            border: `1px solid ${errors.appAccess ? "#d32f2f" : perlColors.border}`,
            borderRadius: "8px",
            padding: 1.2,
            marginBottom: 1.4,
            maxHeight: 160,
            overflowY: "auto",
            backgroundColor: "rgba(255, 255, 255, 0.02)",
          }}
        >
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 600,
              color: perlColors.textPrimary,
              mb: 0.35,
            }}
          >
            Select Commodities (max 2) *
          </Typography>
          <Typography sx={{ fontSize: "11px", color: perlColors.textSecondary, mb: 0.6 }}>
            {signupData.appAccess.length}/{MAX_SIGNUP_COMMODITY_SELECTIONS} selected
          </Typography>

          {signupStatusError && (
            <Typography sx={{ fontSize: "12px", color: perlColors.textSecondary, mb: 1 }}>
              {signupStatusError}
            </Typography>
          )}

          {signupAccessGroups.map((group) => (
            <Box key={group.towerTitle} sx={{ mb: 0.7 }}>
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 600,
                  color: perlColors.textSecondary,
                  mb: 0.15,
                }}
              >
                {group.towerTitle}
              </Typography>
              {group.options.map((option) => (
                <FormControlLabel
                  key={option.accessKey}
                  control={
                    <Checkbox
                      size="small"
                      checked={signupData.appAccess.includes(option.accessKey)}
                      disabled={
                        !signupData.appAccess.includes(option.accessKey) &&
                        signupData.appAccess.length >= MAX_SIGNUP_COMMODITY_SELECTIONS
                      }
                      onChange={() => handleSignupAccessToggle(option.accessKey)}
                      sx={{
                        color: perlColors.textSecondary,
                        padding: "1px 5px",
                        "&.Mui-checked": {
                          color: perlColors.brandColor,
                        },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: "11px", color: perlColors.textPrimary }}>
                      {option.commodityName}
                    </Typography>
                  }
                  sx={{
                    display: "flex",
                    marginLeft: 0,
                    marginRight: 0,
                    minHeight: 22,
                  }}
                />
              ))}
            </Box>
          ))}

          {errors.appAccess && (
            <Typography sx={{ fontSize: "12px", color: "#d32f2f", mt: 0.5 }}>
              {errors.appAccess}
            </Typography>
          )}
        </Box>

        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{
            padding: "8px 18px",
            fontSize: "14px",
            fontWeight: 600,
            backgroundColor: perlColors.brandColor,
            color: perlColors.black,
            textTransform: "none",
            "&:hover": {
              backgroundColor: perlColors.brandColor,
              opacity: 0.9,
            },
          }}
        >
          Submit
        </Button>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          mt: 1.1,
        }}
      >
        <Link
          href="#"
          onClick={(e) => {
            e.preventDefault();
            setView("initial");
          }}
          sx={{
            fontSize: "13px",
            color: perlColors.textSecondary,
            textDecoration: "none",
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          Back to Login
        </Link>
      </Box>
    </>
  );

  const renderAccessRequestedView = () => (
    <Alert
      severity="info"
      sx={{
        backgroundColor: "rgba(33, 150, 243, 0.1)",
        color: perlColors.textPrimary,
        border: "1px solid rgba(33, 150, 243, 0.3)",
        "& .MuiAlert-message": {
          width: "100%",
        },
      }}
    >
      <Typography sx={{ fontSize: "14px", mb: 1 }}>{accessStatusText}</Typography>
      <Typography sx={{ fontSize: "14px" }}>
        For queries, contact{" "}
        <Link
          href="mailto:Admin_Perlab_PL@kearney.com"
          sx={{
            color: perlColors.brandColor,
            textDecoration: "none",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          Admin_Perlab_PL@kearney.com
        </Link>
        <Tooltip title={copied ? "Copied" : "Copy email"}>
          <IconButton
            size="small"
            aria-label="Copy email"
            onClick={() => copyEmail("Admin_Perlab_PL@kearney.com")}
            sx={{
              padding: "0px",
              width: "16px",
              height: "16px",
              fontSize: "14px",
              marginLeft: 0.5,
              color: perlColors.brandColor,
              transition: "transform 120ms ease, opacity 120ms ease",
              transform: copied ? "scale(1.12)" : "scale(1)",
              opacity: copied ? 0.95 : 1,
              "&:hover": {
                backgroundColor: "transparent",
              },
              "&:active": {
                transform: "scale(0.9)",
              },
            }}
          >
            {copied ? <CheckIcon fontSize="inherit" /> : <ContentCopyIcon fontSize="inherit" />}
          </IconButton>
        </Tooltip>
      </Typography>
    </Alert>
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: perlColors.background,
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
      }}
    >
      {/* Left - Login Panel */}
      <Box
        sx={{
          flexBasis: { xs: "100%", md: "40%" },
          maxWidth: { xs: "100%", md: "440px" },
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: {
            xs: perlSpacing.containerSpacing,
            md: perlSpacing.containerSpacing,
          },
          marginLeft: {
            xs: perlSpacing.containerSpacing,
            md: perlSpacing.sectionSpacing,
          },
          marginRight: "auto",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            padding: {
              xs: view === "signup" ? 2 : perlSpacing.containerSpacing,
              md: view === "signup" ? 2 : perlSpacing.containerSpacing,
            },
            backgroundColor: perlColors.surface,
            borderRadius: "12px",
            border: `1px solid ${perlColors.border}`,
          }}
        >
          {/* Logo */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              marginBottom: view === "signup" ? 1.3 : perlSpacing.containerSpacing,
            }}
          >
            <Box
              sx={{
                backgroundColor: "#2A2A2A",
                padding: view === "signup" ? "9px 22px" : "14px 28px",
                borderRadius: "4px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: view === "signup" ? "12px" : "14px",
                  fontWeight: 500,
                  letterSpacing: "2px",
                  color: "#FFFFFF",
                  fontFamily: "'Segoe UI', sans-serif",
                }}
              >
                KEARNEY
              </Typography>
              <Typography
                sx={{
                  fontSize: view === "signup" ? "17px" : "20px",
                  fontWeight: 600,
                  letterSpacing: "1px",
                  color: "#FFFFFF",
                  fontFamily: "'Segoe UI', sans-serif",
                }}
              >
                PERLab
              </Typography>
            </Box>
          </Box>

          {/* Title */}
          <Box sx={{ textAlign: "center", marginBottom: view === "signup" ? 1.2 : 3 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontSize: view === "signup" ? "13px" : "16px",
                color: perlColors.textSecondary,
                lineHeight: 1.2,
              }}
            >
              Welcome to
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontSize: view === "signup" ? "16px" : "18px",
                color: perlColors.textPrimary,
                lineHeight: 1.3,
                marginTop: 0.5,
              }}
            >
              Velocity AI Costing Suite
            </Typography>
          </Box>

          {view !== "accessRequested" && (
            <Typography
              variant="body1"
              sx={{
                textAlign: "center",
                color: perlColors.textSecondary,
                marginBottom: view === "signup" ? 1.3 : 3,
                fontSize: view === "signup" ? "13px" : "14px",
              }}
            >
              {view === "initial" && "Enter your details to get started"}
              {view === "signup" && "Sign up for access"}
            </Typography>
          )}

          {/* Render appropriate view */}
          {view === "initial" && renderInitialView()}
          {view === "signup" && renderSignupView()}
          {view === "accessRequested" && renderAccessRequestedView()}

          {/* Footer Text */}
          {view === "initial" && (
            <Typography
              variant="body2"
              sx={{
                textAlign: "center",
                color: perlColors.textSecondary,
                marginTop: 3,
                fontSize: "12px",
              }}
            >
              By signing in, you agree to our Terms of Service and Privacy Policy
            </Typography>
          )}
        </Paper>
      </Box>

      {/* Right - Image Panel */}
      <Box
        sx={{
          flex: 1,
          minHeight: { xs: "280px", md: "100vh" },
          backgroundImage: "url('/images/Others/login.png')",
          backgroundSize: "contain",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          backgroundColor: perlColors.background,
        }}
      />
    </Box>
  );
};

export default LoginPage;
